import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import {
  AreaIcon,
  BarsIcon,
  BaselineIcon,
  CandlesIcon,
  ColumnIcon,
  HeikenAshiIcon,
  HiLoIcon,
  HLCAreaIcon,
  HollowCandlesIcon,
  LineIcon,
  LineWithMarkersIcon,
  SteplineIcon,
  VolCandleIcon,
} from '../../../assets/chartTypeIcons';
import { SeriesType } from '../../../charting_library';
import { DEFAULT_STARRED_CHART_TYPES } from '../../../constants/tradingview';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import { SelectWithFavorites } from '../../SelectWithFavorites/SelectWithFavorites';

// Chart type groups for MultiSelect
const CHART_TYPE_GROUPS = [
  {
    options: [
      {
        value: SeriesType.Bars,
        label: 'Bars',
        icon: <BarsIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.Candles,
        label: 'Candles',
        icon: <CandlesIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.HollowCandles,
        label: 'Hollow Candles',
        icon: <HollowCandlesIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.VolCandle,
        label: 'Volume Candles',
        icon: <VolCandleIcon className="size-6 shrink-0" />,
      },
    ],
  },
  {
    options: [
      {
        value: SeriesType.Line,
        label: 'Line',
        icon: <LineIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.LineWithMarkers,
        label: 'Line with Markers',
        icon: <LineWithMarkersIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.Stepline,
        label: 'Step Line',
        icon: <SteplineIcon className="size-6 shrink-0" />,
      },
    ],
  },
  {
    options: [
      {
        value: SeriesType.Area,
        label: 'Area',
        icon: <AreaIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.HLCArea,
        label: 'HLC Area',
        icon: <HLCAreaIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.Baseline,
        label: 'Baseline',
        icon: <BaselineIcon className="size-6 shrink-0" />,
      },
    ],
  },
  {
    options: [
      {
        value: SeriesType.Column,
        label: 'Columns',
        icon: <ColumnIcon className="size-6 shrink-0" />,
      },
      {
        value: SeriesType.HiLo,
        label: 'High-Low',
        icon: <HiLoIcon className="size-6 shrink-0" />,
      },
    ],
  },
  {
    options: [
      {
        value: SeriesType.HeikenAshi,
        label: 'Heikin-Ashi',
        icon: <HeikenAshiIcon className="size-6 shrink-0" />,
      },
    ],
  },
];

// Flatten chart type groups into array
const flattenedChartTypes = CHART_TYPE_GROUPS.flatMap((group) =>
  group.options.map((option) => option.value)
);

export const ChartTypeSelector = ({
  value,
  onChange,
}: {
  value: SeriesType;
  onChange: (chartType: SeriesType) => void;
}) => {
  const [starredChartTypes, setStarredChartTypes] = useLocalStorage<SeriesType[]>(
    localStorageKeys.starredChartTypes,
    DEFAULT_STARRED_CHART_TYPES
  );

  const handleToggleStarChartType = useCallback(
    (chartType: SeriesType) => {
      setStarredChartTypes((prev) =>
        prev.includes(chartType)
          ? // Remove star
            prev.filter((t) => t !== chartType)
          : // Add star, and sort based on CHART_TYPE_GROUPS order
            [...prev, chartType].sort(
              (a, b) => flattenedChartTypes.indexOf(a) - flattenedChartTypes.indexOf(b)
            )
      );
    },
    [setStarredChartTypes]
  );

  return (
    <SelectWithFavorites
      options={CHART_TYPE_GROUPS}
      value={value}
      onChange={(value) => (value !== undefined ? onChange(value) : null)}
      starredValues={starredChartTypes}
      onToggleStar={handleToggleStarChartType}
      shortcutComponent={({ option }) => option.icon}
      shortcutButtonClassName="!p-0"
      triggerButtonClassName="size-6"
    />
  );
};
